import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const features = [
  'Real-time transcription',
  'AI-powered question detection',
  'Speaker identification',
  'Download transcripts',
  'Knowledge base integration',
  'Meeting summaries',
];

const Pricing: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="pricing" className="snap-section relative min-h-screen flex items-center py-20 grid-bg">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 w-full">
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">Free for Everyone</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            60 free minutes{' '}
            <span className="gradient-text">every day</span>
          </h2>
          <p className="text-gray-400 mt-4 text-lg max-w-2xl mx-auto">
            Create a free account and get 1 hour of transcription every day. No credit card required, no hidden fees.
          </p>
        </motion.div>

        {/* Free Daily Credits Card */}
        <motion.div
          className="max-w-xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="relative bg-gradient-to-br from-cyan-500/10 to-magenta-500/10 border border-cyan-500/30 rounded-3xl p-8 md:p-10 text-center">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-accent-cyan text-black font-bold text-sm uppercase tracking-wider rounded-full">
              100% Free
            </div>

            {/* Clock Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-accent-cyan" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="12,6 12,12 16,14" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Credits Display */}
            <div className="text-6xl md:text-7xl font-bold text-white mb-2">
              60
            </div>
            <div className="text-gray-400 text-lg uppercase tracking-wider mb-6">
              minutes / day
            </div>

            <p className="text-gray-300 mb-8 max-w-sm mx-auto">
              That's 1 full hour of real-time transcription, renewed every day. Perfect for interviews, meetings, and lectures.
            </p>

            <a
              href="/login"
              className="inline-block px-8 py-4 bg-accent-cyan text-black font-bold text-lg rounded-full hover:bg-white transition-colors"
            >
              Create Free Account
            </a>

            <p className="text-gray-500 text-sm mt-4">
              No credit card required
            </p>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3 className="text-center text-white font-semibold mb-6">Everything included:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature) => (
              <div key={feature} className="flex items-center gap-2 text-gray-400 text-sm">
                <svg className="w-4 h-4 text-accent-cyan flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {feature}
              </div>
            ))}
          </div>
        </motion.div>

        {/* How it works */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-center justify-center gap-8 text-gray-500 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-accent-cyan flex items-center justify-center text-xs font-bold">1</span>
              Create account
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-accent-cyan flex items-center justify-center text-xs font-bold">2</span>
              Claim daily credits
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-accent-cyan flex items-center justify-center text-xs font-bold">3</span>
              Start transcribing
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
